import { supabase } from '../lib/supabase';

export const pushNotificationService = {
  /**
   * Register device for push notifications
   */
  async registerDevice(deviceData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

      if (!employeeData) throw new Error('User not associated with a company');

      // Check if device already registered
      const { data: existingDevice } = await supabase?.from('push_notification_devices')?.select('id')?.eq('device_token', deviceData?.deviceToken)?.eq('user_id', user?.id)?.single();

      if (existingDevice) {
        // Update existing device
        const { data, error } = await supabase?.from('push_notification_devices')?.update({
            device_platform: deviceData?.devicePlatform,
            device_name: deviceData?.deviceName,
            device_model: deviceData?.deviceModel,
            os_version: deviceData?.osVersion,
            app_version: deviceData?.appVersion,
            is_active: true,
            last_active_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString()
          })?.eq('id', existingDevice?.id)?.select()?.single();

        if (error) throw error;
        return this.transformDevice(data);
      }

      // Register new device
      const { data, error } = await supabase?.from('push_notification_devices')?.insert({
          user_id: user?.id,
          company_id: employeeData?.company_id,
          device_token: deviceData?.deviceToken,
          device_platform: deviceData?.devicePlatform,
          device_name: deviceData?.deviceName,
          device_model: deviceData?.deviceModel,
          os_version: deviceData?.osVersion,
          app_version: deviceData?.appVersion,
          metadata: deviceData?.metadata || {}
        })?.select()?.single();

      if (error) throw error;
      return this.transformDevice(data);
    } catch (error) {
      console.error('Error registering device:', error);
      throw error;
    }
  },

  /**
   * Unregister device
   */
  async unregisterDevice(deviceToken) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('push_notification_devices')?.update({ is_active: false, updated_at: new Date()?.toISOString() })?.eq('device_token', deviceToken)?.eq('user_id', user?.id);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error unregistering device:', error);
      throw error;
    }
  },

  /**
   * Get user's registered devices
   */
  async getUserDevices() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('push_notification_devices')?.select('*')?.eq('user_id', user?.id)?.eq('is_active', true)?.order('last_active_at', { ascending: false });

      if (error) throw error;
      return data?.map(device => this.transformDevice(device)) || [];
    } catch (error) {
      console.error('Error fetching user devices:', error);
      throw error;
    }
  },

  /**
   * Send push notification to specific users
   */
  async sendNotification(notificationData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

      if (!employeeData) throw new Error('User not associated with a company');

      // Get active devices for target users
      const { data: devices, error: devicesError } = await supabase?.from('push_notification_devices')?.select('id, user_id')?.in('user_id', notificationData?.userIds)?.eq('is_active', true);

      if (devicesError) throw devicesError;

      if (!devices || devices?.length === 0) {
        return { success: false, message: 'No active devices found for target users' };
      }

      // Create delivery records for each device
      const deliveries = devices?.map(device => ({
        device_id: device?.id,
        user_id: device?.user_id,
        company_id: employeeData?.company_id,
        notification_type: notificationData?.type,
        title: notificationData?.title,
        body: notificationData?.body,
        data: notificationData?.data || {},
        priority: notificationData?.priority || 'normal',
        confirmation_required: notificationData?.requiresConfirmation || false
      }));

      const { data, error } = await supabase?.from('push_notification_deliveries')?.insert(deliveries)?.select();

      if (error) throw error;

      return {
        success: true,
        deliveriesCreated: data?.length || 0,
        deliveries: data?.map(d => this.transformDelivery(d)) || []
      };
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  },

  /**
   * Send notification using template
   */
  async sendTemplatedNotification(templateName, userIds, variables) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

      if (!employeeData) throw new Error('User not associated with a company');

      // Get template
      const { data: template, error: templateError } = await supabase?.from('push_notification_templates')?.select('*')?.eq('company_id', employeeData?.company_id)?.eq('template_name', templateName)?.eq('is_active', true)?.single();

      if (templateError) throw templateError;
      if (!template) throw new Error(`Template "${templateName}" not found`);

      // Replace variables in title and body
      let title = template?.title_template;
      let body = template?.body_template;

      Object.keys(variables)?.forEach(key => {
        const placeholder = `{{${key}}}`;
        title = title?.replace(new RegExp(placeholder, 'g'), variables?.[key]);
        body = body?.replace(new RegExp(placeholder, 'g'), variables?.[key]);
      });

      // Send notification
      return await this.sendNotification({
        userIds,
        type: template?.notification_type,
        title,
        body,
        priority: template?.priority,
        requiresConfirmation: template?.requires_confirmation,
        data: {
          templateName: template?.template_name,
          actionButtons: template?.action_buttons,
          variables
        }
      });
    } catch (error) {
      console.error('Error sending templated notification:', error);
      throw error;
    }
  },

  /**
   * Confirm notification delivery
   */
  async confirmDelivery(deliveryId, responseData = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('push_notification_deliveries')?.update({
          confirmed_at: new Date()?.toISOString(),
          confirmed_by: user?.id,
          response_data: responseData,
          delivery_status: 'delivered',
          updated_at: new Date()?.toISOString()
        })?.eq('id', deliveryId)?.eq('user_id', user?.id)?.select()?.single();

      if (error) throw error;
      return this.transformDelivery(data);
    } catch (error) {
      console.error('Error confirming delivery:', error);
      throw error;
    }
  },

  /**
   * Mark notification as read
   */
  async markAsRead(deliveryId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('push_notification_deliveries')?.update({
          read_at: new Date()?.toISOString(),
          delivery_status: 'read',
          updated_at: new Date()?.toISOString()
        })?.eq('id', deliveryId)?.eq('user_id', user?.id)?.select()?.single();

      if (error) throw error;
      return this.transformDelivery(data);
    } catch (error) {
      console.error('Error marking as read:', error);
      throw error;
    }
  },

  /**
   * Get user's notification deliveries
   */
  async getUserDeliveries(filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase?.from('push_notification_deliveries')?.select(`
          *,
          device:push_notification_devices(device_name, device_platform),
          confirmed_by_user:user_profiles!push_notification_deliveries_confirmed_by_fkey(full_name, email)
        `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('delivery_status', filters?.status);
      }

      if (filters?.type) {
        query = query?.eq('notification_type', filters?.type);
      }

      if (filters?.unreadOnly) {
        query = query?.is('read_at', null);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data?.map(delivery => this.transformDelivery(delivery)) || [];
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      throw error;
    }
  },

  /**
   * Get delivery statistics for supervisor
   */
  async getDeliveryStats(companyId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (!companyId) {
        const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

        if (!employeeData) throw new Error('User not associated with a company');
        companyId = employeeData?.company_id;
      }

      const { data, error } = await supabase?.from('push_notification_deliveries')?.select('delivery_status, priority, notification_type')?.eq('company_id', companyId)?.gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString());

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        byStatus: {},
        byPriority: {},
        byType: {},
        deliveryRate: 0
      };

      data?.forEach(delivery => {
        stats.byStatus[delivery.delivery_status] = (stats?.byStatus?.[delivery?.delivery_status] || 0) + 1;
        stats.byPriority[delivery.priority] = (stats?.byPriority?.[delivery?.priority] || 0) + 1;
        stats.byType[delivery.notification_type] = (stats?.byType?.[delivery?.notification_type] || 0) + 1;
      });

      const delivered = (stats?.byStatus?.delivered || 0) + (stats?.byStatus?.read || 0);
      stats.deliveryRate = stats?.total > 0 ? Math.round((delivered / stats?.total) * 100) : 0;

      return stats;
    } catch (error) {
      console.error('Error fetching delivery stats:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time delivery updates
   */
  subscribeToDeliveries(userId, callback) {
    const channel = supabase?.channel(`push-deliveries-${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'push_notification_deliveries',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Push delivery change:', payload);
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformDelivery(payload?.new) : null,
            old: payload?.old ? this.transformDelivery(payload?.old) : null
          };
          callback(transformedPayload);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Transform device data from snake_case to camelCase
   */
  transformDevice(device) {
    if (!device) return null;
    return {
      id: device?.id,
      userId: device?.user_id,
      companyId: device?.company_id,
      deviceToken: device?.device_token,
      devicePlatform: device?.device_platform,
      deviceName: device?.device_name,
      deviceModel: device?.device_model,
      osVersion: device?.os_version,
      appVersion: device?.app_version,
      isActive: device?.is_active,
      lastActiveAt: device?.last_active_at,
      registeredAt: device?.registered_at,
      metadata: device?.metadata,
      createdAt: device?.created_at,
      updatedAt: device?.updated_at
    };
  },

  /**
   * Transform delivery data from snake_case to camelCase
   */
  transformDelivery(delivery) {
    if (!delivery) return null;
    return {
      id: delivery?.id,
      deviceId: delivery?.device_id,
      userId: delivery?.user_id,
      companyId: delivery?.company_id,
      notificationType: delivery?.notification_type,
      title: delivery?.title,
      body: delivery?.body,
      data: delivery?.data,
      priority: delivery?.priority,
      deliveryStatus: delivery?.delivery_status,
      sentAt: delivery?.sent_at,
      deliveredAt: delivery?.delivered_at,
      readAt: delivery?.read_at,
      failedAt: delivery?.failed_at,
      errorMessage: delivery?.error_message,
      retryCount: delivery?.retry_count,
      maxRetries: delivery?.max_retries,
      expiresAt: delivery?.expires_at,
      confirmationRequired: delivery?.confirmation_required,
      confirmedAt: delivery?.confirmed_at,
      confirmedBy: delivery?.confirmed_by,
      confirmedByUser: delivery?.confirmed_by_user,
      responseData: delivery?.response_data,
      device: delivery?.device,
      createdAt: delivery?.created_at,
      updatedAt: delivery?.updated_at
    };
  }
};